import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  DatePicker,
  AutoComplete,
  Select,
  message,
  Input,
  Divider,
  TimePicker,
  Space,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { IconGenerate, IconPrint, IconBillSave } from "Common/ReactIcons";
import dayjs from "dayjs";
import utils from "utils";
import AutoCompleteDebounce from "Components/AutoCompleteDebounce";
import TextArea from "antd/es/input/TextArea";
import api from "utils/api";
const { Option } = Select;

const PatientForm = ({
  form,
  fieldOptions,
  selectedPatientData,
  handleAutocompleteChange,
  handleAutocompleteSelect,
  editData,
  showReferenceIdModal,
}) => {
  const [doctorsList, setDoctorsList] = useState([]);
  const [consultationDates, setConsultationDates] = useState([]);

  useEffect(() => {
    const updateFields = async () => {
      if (selectedPatientData?.patient_id) {
        form.setFieldsValue({
          name: selectedPatientData.name || "",
          contact_number: selectedPatientData.contact_number || "",
          patient_id: selectedPatientData.patient_id || "",
        });
        await setDoctorOption(selectedPatientData.patient_id);
      }
    };

    updateFields();
  }, [selectedPatientData, form]);
  useEffect(async () => {
    if (editData) {
      let doctorItems = await setDoctorOption(editData?.patient_id || "");
      const selectedDoctor = doctorItems.find(
        (doc) => doc.value === (editData.doctor_id || "")
      );
      setConsultationDates(selectedDoctor?.consultations || []);
      form.setFieldsValue({
        doctor_name: editData.doctor_id || undefined,
        appointment_date: editData.appointment_id || undefined,
      });
    }
  }, [editData]);

  async function setDoctorOption(patient_id = "") {
    try {
      const query = new URLSearchParams({ patient_id }).toString();
      const response = await api.fetchDoctorSearch(query);
      if (response.data.status === 200) {
        const results = response.data.data.results;
        const data = results.map((item) => ({
          name: item.doctor_name,
          value: item.doctor_id,
          specialization: item.doctor_specialty,
          salutation: item.doctor_salutation,
          consultations: item.consultation_date_time,
        }));
        setDoctorsList(data);
        return data;
      } else {
        message.error(response.data.message);
      }
    } catch (err) {
      console.error(err);
    }
  }

  const handleDoctorChange = (value) => {
    const selectedDoctor = doctorsList.find((doc) => doc.value === value);
    setConsultationDates(selectedDoctor?.consultations || []);
    form.setFieldsValue({ doctor_name: value, appointment_date: undefined });
  };

  return (
    <>
      <Form.Item name="billing_date" label="Bill Date" initialValue={dayjs()}>
        <DatePicker
          style={{ width: "100%" }}
          format="DD-MM-YYYY"
          disabledDate={(current) =>
            current && current < dayjs().startOf("day")
          }
        />
      </Form.Item>
      <Form.Item
        name="name"
        label="Name"
        rules={[{ required: true, message: "Patient name is required" }]}
      >
        <AutoCompleteDebounce
          options={fieldOptions.name.options}
          onSelect={(value, option) =>
            handleAutocompleteSelect("name", value, option)
          }
          onSearch={(value) => handleAutocompleteChange("name", value)}
          placeholder="Select or type to search"
          loading={fieldOptions.name.loading}
        />
      </Form.Item>
      <Form.Item
        name="contact_number"
        label="Contact No"
        rules={[{ required: true, message: "Contact no is required" }]}
      >
        <AutoCompleteDebounce
          options={fieldOptions.contact_number.options}
          onSelect={(value, option) =>
            handleAutocompleteSelect("contact_number", value, option)
          }
          onSearch={(value) =>
            handleAutocompleteChange("contact_number", value)
          }
          placeholder="Select or type to search"
          loading={fieldOptions.contact_number.loading}
        />
      </Form.Item>
      <Form.Item
        name="patient_id"
        label="Patient ID"
        rules={[{ required: true, message: "Patient id is required" }]}
      >
        <AutoCompleteDebounce
          options={fieldOptions.patient_id.options}
          onSelect={(value, option) => {
            form.resetFields(["doctor_name", "appointment_date"]);
            handleAutocompleteSelect("patient_id", value, option);
          }}
          onSearch={(value) => handleAutocompleteChange("patient_id", value)}
          placeholder="Select or type to search"
          loading={fieldOptions.patient_id.loading}
        />
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => (
          <Form.Item
            name="payment_mode"
            label="Payment Mode"
            rules={[
              {
                required: getFieldValue("payment_status") === "Paid",
                message: "Payment mode is required",
              },
            ]}
          >
            <Select disabled={false}>
              <Option value="CASH">Cash</Option>
              <Option value="CARD">Card</Option>
              <Option value="UPI">UPI</Option>
              <Option value="CHEQUE">Cheque</Option>
              <Option value="Net Banking">Net Banking</Option>
            </Select>
          </Form.Item>
        )}
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => (
          <Form.Item
            name="reference_id"
            // label="Reference ID"
            label={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Space>
                  <span>Reference ID</span>
                  {(editData?.is_generated &&
                    editData?.payment_status === "Partially paid" && editData?.payment_details?.[0]?.paid_date) && (
                      <span
                        style={{ color: "blue", cursor: "pointer" }}
                        onClick={showReferenceIdModal}
                      >
                        Click
                      </span>
                    )}
                </Space>
              </div>
            }
            rules={[
              {
                required:
                  getFieldValue("payment_mode") &&
                  getFieldValue("payment_mode") !== "CASH" &&
                  (getFieldValue("payment_status") == "Paid" ||
                    getFieldValue("payment_status") == "Partially paid"),
                message: "Reference ID is required",
              },
            ]}
          >
            <Input disabled={getFieldValue("payment_mode") == "CASH"} />
          </Form.Item>
        )}
      </Form.Item>
      <Form.Item
        name="payment_status"
        label="Payment Status"
        initialValue={"Draft"}
        rules={[{ required: true }]}
      >
        <Select disabled={false}>
          {!editData?.is_generated && <Option value="Draft">Draft</Option>}
          {editData?.payment_status !== "Partially paid" && (
            <Option value="Pending">Pending</Option>
          )}
          <Option value="Paid">Paid</Option>
          <Option value="Partially paid">Partially paid</Option>
          {editData?.payment_status &&
            editData?.payment_status !== "Draft" &&
            editData?.payment_status !== "Partially paid" && (
              <Option value="Cancelled">Cancelled</Option>
            )}
        </Select>
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) =>
          getFieldValue("payment_status") === "Cancelled" && (
            <Form.Item
              name="reason_for_cancel"
              label="Reason for Cancel"
              rules={[
                {
                  required: getFieldValue("payment_status") === "Cancelled",
                  message: "This field cannot be empty",
                },
              ]}
            >
              <TextArea disabled={false} />
            </Form.Item>
          )
        }
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => (
          <Form.Item
            name="doctor_name"
            label="Doctor Name"
            rules={[{ required: true, message: "Doctor Name is required" }]}
          >
            <Select
              showSearch
              allowClear
              placeholder="Choose a doctor"
              optionLabelProp="label"
              onChange={handleDoctorChange}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              disabled={!getFieldValue("patient_id") || editData?.is_generated}
            >
              {doctorsList.map((item) => (
                <Option key={item.value} value={item.value} label={item.name}>
                  <div className="option-section">
                    <span className="name">
                      {item.salutation} {item.name}
                    </span>
                    {item.specialization && (
                      <span
                        className="doctor-specl-regno"
                        style={{
                          display: "inline-block",
                          wordWrap: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {` ${item.specialization.join(", ")}`}
                      </span>
                    )}
                  </div>
                  <Divider style={{ margin: "0px", padding: "0px" }} />
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => {
          const isDateAndTimeRequired = consultationDates.length > 0;

          return (
            <Form.Item
              name="appointment_date"
              label="Date and Time"
              rules={[
                // { required: isDateAndTimeRequired, message: "Date and Time is required" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (isDateAndTimeRequired && !value) {
                      return Promise.reject(
                        new Error("Date and Time is required")
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Select
                placeholder="Select date and time"
                style={{ width: "100%" }}
                disabled={
                  !getFieldValue("doctor_name") ||
                  consultationDates.length === 0 ||
                  editData?.is_generated
                }
              >
                {consultationDates.map((appointment) => (
                  <Select.Option
                    key={appointment.appointment_id}
                    value={appointment.appointment_id}
                  >
                    {utils.FormatDate(appointment.appointment_date, "DATE_14")}
                    &nbsp;
                    {utils.FormatDate(appointment.appointment_date, "TIME_1")}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          );
        }}
      </Form.Item>
    </>
  );
};

export default PatientForm;
